import React from 'react';
import { motion } from 'framer-motion';
import { Framework } from '../data/frameworks';
import { BookOpen, User, Target, Lightbulb, Pencil } from 'lucide-react';
import { useFrameworks } from '../context/FrameworksContext';
interface FrameworkDetailProps {
  framework: Framework | null;
  isCmsMode: boolean;
  onEdit: () => void;
}
export function FrameworkDetail({
  framework,
  isCmsMode,
  onEdit
}: FrameworkDetailProps) {
  const {
    categoryColors
  } = useFrameworks();
  if (!framework) {
    return <div className="flex-1 flex items-center justify-center p-8 text-slate-500 dark:text-slate-400" data-id="element-191">
        <div className="text-center max-w-md" data-id="element-192">
          <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-20" data-id="element-193" />
          <h2 className="text-xl font-medium mb-2 text-slate-700 dark:text-slate-300" data-id="element-194">
            Select a Framework
          </h2>
          <p className="text-sm" data-id="element-195">
            Choose a storytelling framework from the sidebar to explore its
            structure, history, and examples.
          </p>
        </div>
      </div>;
  }
  return <motion.div key={framework.id} initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3
  }} className="flex-1 overflow-y-auto" data-id="element-196">
      <div className="max-w-4xl mx-auto px-6 py-10 md:px-10 md:py-16" data-id="element-197">
        {/* Header Section */}
        <header className="mb-12 relative" data-id="element-198">
          {isCmsMode && <button onClick={onEdit} className="absolute top-0 right-0 flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 dark:text-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-md transition-colors" data-id="element-199">
              <Pencil className="w-4 h-4" data-id="element-200" />
              Edit Framework
            </button>}
          <div className="flex flex-wrap items-center gap-3 mb-4" data-id="element-201">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${categoryColors[framework.category] || 'bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700'}`} data-id="element-202">
              {framework.category}
            </span>
            <div className="flex items-center text-sm text-slate-500 dark:text-slate-400" data-id="element-203">
              <User className="w-4 h-4 mr-1.5" data-id="element-204" />
              {framework.origin}
            </div>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white tracking-tight mb-6" data-id="element-205">
            {framework.name}
          </h1>

          <p className="text-xl text-slate-600 dark:text-slate-300 leading-relaxed font-medium" data-id="element-206">
            {framework.summary}
          </p>
        </header>

        {/* Description */}
        <section className="mb-12 prose prose-slate dark:prose-invert max-w-none" data-id="element-207">
          <p className="text-base md:text-lg leading-relaxed text-slate-700 dark:text-slate-300" data-id="element-208">
            {framework.description}
          </p>
        </section>

        {/* Structure / Steps */}
        <section className="mb-14" data-id="element-209">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6 flex items-center" data-id="element-210">
            <Target className="w-6 h-6 mr-2 text-blue-500" data-id="element-211" />
            The Structure
          </h2>

          <div className="space-y-4" data-id="element-212">
            {framework.steps.map((step, index) => <div key={index} className="flex bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow" data-id="element-213">
                <div className="flex-shrink-0 mr-5" data-id="element-214">
                  <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold text-sm" data-id="element-215">
                    {index + 1}
                  </div>
                </div>
                <div data-id="element-216">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2" data-id="element-217">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed" data-id="element-218">
                    {step.description}
                  </p>
                </div>
              </div>)}
          </div>
        </section>

        {/* Example Block */}
        <section className="mb-12" data-id="element-219">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6 flex items-center" data-id="element-220">
            <Lightbulb className="w-6 h-6 mr-2 text-amber-500" data-id="element-221" />
            Example in Action
          </h2>
          <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl p-6 md:p-8 relative overflow-hidden" data-id="element-222">
            <div className="absolute top-0 left-0 w-1 h-full bg-amber-400 dark:bg-amber-500" data-id="element-223"></div>
            <p className="text-slate-700 dark:text-slate-300 italic leading-relaxed" data-id="element-224">
              "{framework.example}"
            </p>
          </div>
        </section>

        {/* Best For Tags */}
        <section className="pt-8 border-t border-slate-200 dark:border-slate-800" data-id="element-225">
          <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4" data-id="element-226">
            Best Used For
          </h3>
          <div className="flex flex-wrap gap-2" data-id="element-227">
            {framework.bestFor.map((tag, index) => <span key={index} className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-md text-sm font-medium" data-id="element-228">
                {tag}
              </span>)}
          </div>
        </section>
      </div>
    </motion.div>;
}